import { useT, type Translate } from "../../../../shared/i18n/i18n";
import { offset, useFloating } from "@floating-ui/react-dom";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { TableMap } from "@tiptap/pm/tables";
import {
  ArrowDown,
  ArrowDownAZ,
  ArrowDownToLine,
  ArrowLeft,
  ArrowLeftToLine,
  ArrowRight,
  ArrowRightToLine,
  ArrowUp,
  ArrowUpAZ,
  ArrowUpToLine,
  Copy,
  Eraser,
  Merge,
  Plus,
  Split,
  Table2,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../../shared/lib/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  colDragStart,
  freezeHandles,
  hideTableHandles,
  rowDragStart,
  tableDragEnd,
  unfreezeHandles,
  type TableHandlesState,
  type TableHandlesStorage,
} from "./table-handle-extension";
import {
  addRowColumn,
  canDuplicateRowColumn,
  canMoveRowColumn,
  canSortColumn,
  clearRowColumnContent,
  deleteRowColumn,
  duplicateRowColumn,
  isHeaderRowColumn,
  moveRowColumn,
  removeTable,
  selectRowOrColumn,
  sortColumn,
  toggleHeaderRowColumn,
  type MoveDirection,
} from "./table-actions";
import {
  countEmptyColumnsFromEnd,
  countEmptyRowsFromEnd,
  EMPTY_CELL_HEIGHT,
  EMPTY_CELL_WIDTH,
  marginRound,
  runPreservingCursor,
  selectLastCell,
  type Orientation,
} from "./table-utils";

// The mnemos/Notion table UX: hovering a cell raises a pill handle above its
// column and another beside its row. Each opens a menu scoped to that exact
// row/column; dragging a handle moves the row/column with a drop indicator.
// "+" rails on the right/bottom edges append (drag adds/removes in bulk).

function useTableHandlesState(editor: Editor): TableHandlesState | null {
  const [state, setState] = useState<TableHandlesState | null>(null);

  useEffect(() => {
    const storage = (editor.storage as unknown as Record<string, TableHandlesStorage | undefined>).tableHandles;
    if (!storage) return;
    setState(storage.state);
    storage.onStateChange = setState;
    return () => {
      if (storage.onStateChange === setState) storage.onStateChange = null;
    };
  }, [editor]);

  return state;
}

type DraggingState = TableHandlesState["draggingState"];

// Virtual reference rects, straight from the mnemos positioning hook: the row
// handle references a rect spanning the row's full width, the column handle
// one spanning the column's full height; during a drag they follow the mouse.
function handleReferenceRect(
  orientation: "col" | "row",
  cell: DOMRect,
  table: DOMRect,
  dragging: DraggingState,
): DOMRect {
  if (orientation === "row") {
    if (dragging?.draggedCellOrientation === "row") {
      const adjustedY = dragging.mousePos + dragging.initialOffset;
      return new DOMRect(table.x, Math.max(table.y, Math.min(adjustedY, table.bottom - cell.height)), table.width, cell.height);
    }
    return new DOMRect(table.x, cell.y, table.width, cell.height);
  }
  if (dragging?.draggedCellOrientation === "col") {
    const adjustedX = dragging.mousePos + dragging.initialOffset;
    return new DOMRect(Math.max(table.x, Math.min(adjustedX, table.right - cell.width)), table.y, cell.width, table.height);
  }
  return new DOMRect(cell.x, table.y, cell.width, table.height);
}

// A 2×2 dot grid, the AFFiNE-style drag affordance.
function DotsSquareIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden className={className} fill="currentColor" viewBox="0 0 10 10">
      <circle cx="3.2" cy="3.2" r="1.1" />
      <circle cx="6.8" cy="3.2" r="1.1" />
      <circle cx="3.2" cy="6.8" r="1.1" />
      <circle cx="6.8" cy="6.8" r="1.1" />
    </svg>
  );
}

function useHandlePosition(
  orientation: "col" | "row",
  show: boolean,
  cellRect: DOMRect | null,
  tableRect: DOMRect | null,
  dragging: DraggingState,
) {
  const floating = useFloating({
    // The compact handle is 14px along the main axis; -7 centers it exactly
    // on the table's border line (half in, half out), AFFiNE-style.
    middleware: [offset(-7)],
    open: show,
    placement: orientation === "row" ? "left" : "top",
    strategy: "fixed",
  });

  const { refs, update } = floating;
  useEffect(() => {
    if (!cellRect || !tableRect) return;
    refs.setReference({
      getBoundingClientRect: () => handleReferenceRect(orientation, cellRect, tableRect, dragging),
    });
    update();
  }, [cellRect, dragging, orientation, refs, tableRect, update]);

  return floating;
}

// Подписи меню строятся по языку интерфейса, а не лежат константой: раньше это
// был модульный объект с английскими строками, который переводу не поддавался
// в принципе.
function menuLabels(orientation: Orientation, t: Translate) {
  const prefix = orientation === "row" ? "row" : "column";
  return {
    addAfter: t(`docMenu.${prefix}AddAfter`),
    addBefore: t(`docMenu.${prefix}AddBefore`),
    clear: t(`docMenu.${prefix}Clear`),
    delete: t(`docMenu.${prefix}Delete`),
    duplicate: t(`docMenu.${prefix}Duplicate`),
    header: orientation === "row" ? t("docTable.headerRow") : t("docTable.headerColumn"),
    moveAfter: t(`docMenu.${prefix}MoveAfter`),
    moveBefore: t(`docMenu.${prefix}MoveBefore`),
  };
}

function HandleMenu({
  editor,
  index,
  onOpenChange,
  open,
  orientation,
  tablePos,
}: {
  editor: Editor;
  index: number;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  orientation: Orientation;
  tablePos: number;
}) {
  const t = useT();
  const context = { editor, index, orientation, tablePos };
  const labels = menuLabels(orientation, t);
  const isRow = orientation === "row";
  const beforeDirection: MoveDirection = isRow ? "up" : "left";
  const afterDirection: MoveDirection = isRow ? "down" : "right";

  const isHeader = isHeaderRowColumn(context);
  const canMoveBefore = canMoveRowColumn(context, beforeDirection);
  const canMoveAfter = canMoveRowColumn(context, afterDirection);
  const canDuplicate = canDuplicateRowColumn(context);
  const canSort = !isRow && canSortColumn(context);

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      onOpenChange(isOpen);
      if (isOpen) {
        freezeHandles(editor);
        selectRowOrColumn(context);
      } else {
        unfreezeHandles(editor);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, index, onOpenChange, orientation, tablePos],
  );

  const run = (action: () => void) => () => {
    action();
  };

  const [isDragging, setIsDragging] = useState(false);

  return (
    <DropdownMenu onOpenChange={handleToggle} open={open}>
      <DropdownMenuTrigger
        render={
          <button
            aria-label={isRow ? t("docTable.rowActions") : t("docTable.columnActions")}
            className={cn("document-table-handle", isRow ? "document-table-handle--row" : "document-table-handle--col", open && "is-open", isDragging && "is-dragging")}
            draggable
            onDragEnd={() => {
              setIsDragging(false);
              tableDragEnd(editor);
            }}
            onDragStart={(event) => {
              setIsDragging(true);
              if (isRow) rowDragStart(editor, event);
              else colDragStart(editor, event);
            }}
            type="button"
          >
            <DotsSquareIcon className="size-2.5" />
          </button>
        }
      />
      <DropdownMenuContent align="start" className="w-48" side={isRow ? "left" : "bottom"}>
        {index === 0 ? (
          <>
            <DropdownMenuItem className={cn(isHeader && "bg-accent/60")} onClick={run(() => toggleHeaderRowColumn(context))}>
              <Table2 data-icon="inline-start" />
              {labels.header}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem disabled={!canMoveBefore} onClick={run(() => moveRowColumn(context, beforeDirection))}>
          {isRow ? <ArrowUp data-icon="inline-start" /> : <ArrowLeft data-icon="inline-start" />}
          {labels.moveBefore}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!canMoveAfter} onClick={run(() => moveRowColumn(context, afterDirection))}>
          {isRow ? <ArrowDown data-icon="inline-start" /> : <ArrowRight data-icon="inline-start" />}
          {labels.moveAfter}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={run(() => addRowColumn(context, "before"))}>
          {isRow ? <ArrowUpToLine data-icon="inline-start" /> : <ArrowLeftToLine data-icon="inline-start" />}
          {labels.addBefore}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={run(() => addRowColumn(context, "after"))}>
          {isRow ? <ArrowDownToLine data-icon="inline-start" /> : <ArrowRightToLine data-icon="inline-start" />}
          {labels.addAfter}
        </DropdownMenuItem>
        {canSort ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={run(() => sortColumn(context, "asc"))}>
              <ArrowDownAZ data-icon="inline-start" />
              {t("editor.sortAscending")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={run(() => sortColumn(context, "desc"))}>
              <ArrowUpAZ data-icon="inline-start" />
              {t("editor.sortDescending")}
            </DropdownMenuItem>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!canDuplicate} onClick={run(() => duplicateRowColumn(context))}>
          <Copy data-icon="inline-start" />
          {labels.duplicate}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={run(() => clearRowColumnContent(context))}>
          <Eraser data-icon="inline-start" />
          {labels.clear}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={run(() => deleteRowColumn(context))} variant="destructive">
          <Trash2 data-icon="inline-start" />
          {labels.delete}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={run(() => removeTable(context))} variant="destructive">
          <Trash2 data-icon="inline-start" />
          {t("editor.deleteTable")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Edge "+" button: click appends one row/column, dragging adds or removes
// (only trailing empty ones) in bulk.
function ExtendButton({
  editor,
  onDragStateChange,
  orientation,
  state,
  tableSize,
}: {
  editor: Editor;
  onDragStateChange: (dragging: boolean) => void;
  orientation: Orientation;
  state: TableHandlesState;
  tableSize: number;
}) {
  const t = useT();
  const isRow = orientation === "row";
  const movedRef = useRef(false);
  const [dragState, setDragState] = useState<{ originalHeight: number; originalWidth: number; startPos: number } | null>(null);

  const handleClick = useCallback(() => {
    if (movedRef.current) return;
    runPreservingCursor(editor, () => {
      selectLastCell(editor, state.block, state.blockPos, orientation);
      if (isRow) editor.commands.addRowAfter();
      else editor.commands.addColumnAfter();
    });
  }, [editor, isRow, orientation, state.block, state.blockPos]);

  const startDrag = useCallback(
    (event: React.MouseEvent) => {
      const dims = TableMap.get(state.block);
      movedRef.current = false;
      setDragState({
        originalHeight: dims.height,
        originalWidth: dims.width,
        startPos: isRow ? event.clientY : event.clientX,
      });
      freezeHandles(editor);
      onDragStateChange(true);
      event.preventDefault();
    },
    [editor, isRow, onDragStateChange, state.block],
  );

  useEffect(() => {
    if (!dragState) return;

    const handleMove = (event: MouseEvent) => {
      movedRef.current = true;
      const currentPos = isRow ? event.clientY : event.clientX;
      const diff = currentPos - dragState.startPos;
      const cellSize = isRow ? EMPTY_CELL_HEIGHT : EMPTY_CELL_WIDTH;

      const currentDims = TableMap.get(state.block);
      const currentCount = isRow ? currentDims.height : currentDims.width;
      const originalCount = isRow ? dragState.originalHeight : dragState.originalWidth;
      const newCount = Math.max(1, originalCount + marginRound(diff / cellSize, 0.3));
      const delta = newCount - currentCount;
      if (delta === 0) return;

      runPreservingCursor(editor, () => {
        if (delta > 0) {
          selectLastCell(editor, state.block, state.blockPos, orientation);
          for (let i = 0; i < delta; i += 1) {
            if (isRow) editor.commands.addRowAfter();
            else editor.commands.addColumnAfter();
          }
        } else {
          const emptyCount = isRow ? countEmptyRowsFromEnd(editor, state.blockPos) : countEmptyColumnsFromEnd(editor, state.blockPos);
          const safeToRemove = Math.min(Math.abs(delta), emptyCount, currentCount - 1);
          selectLastCell(editor, state.block, state.blockPos, orientation);
          for (let i = 0; i < safeToRemove; i += 1) {
            if (isRow) editor.commands.deleteRow();
            else editor.commands.deleteColumn();
          }
        }
      });
    };

    const handleUp = () => {
      setDragState(null);
      unfreezeHandles(editor);
      onDragStateChange(false);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragState, editor, isRow, onDragStateChange, orientation, state.block, state.blockPos]);

  return (
    <button
      aria-label={isRow ? t("docTable.addRemoveRows") : t("docTable.addRemoveColumns")}
      className={cn(
        "document-table-extend",
        isRow ? "document-table-extend--row" : "document-table-extend--col",
        dragState && "is-editing",
      )}
      onClick={handleClick}
      onMouseDown={startDrag}
      style={isRow ? { width: tableSize } : { height: tableSize }}
      type="button"
    >
      <Plus className="size-3" />
    </button>
  );
}

function useExtendPosition(orientation: Orientation, show: boolean, tableRect: DOMRect | null) {
  const isRow = orientation === "row";
  const floating = useFloating({
    middleware: [offset(4)],
    open: show,
    placement: isRow ? "bottom" : "right",
    strategy: "fixed",
  });

  const { refs, update } = floating;
  useEffect(() => {
    if (!tableRect) return;
    refs.setReference({ getBoundingClientRect: () => tableRect });
    update();
  }, [refs, tableRect, update]);

  return floating;
}

// Merge/split control floating at the bottom-right of a cell selection —
// prosemirror-tables commands, positioned off the .selectedCell DOM rects.
function CellSelectionControls({ editor }: { editor: Editor }) {
  const t = useT();
  const [, forceTick] = useState(0);
  const selectionState = useEditorState({
    editor,
    selector: (context) => {
      const instance = context.editor;
      if (!instance) return null;
      const canMerge = instance.can().mergeCells();
      const canSplit = instance.can().splitCell();
      if (!canMerge && !canSplit) return null;
      return { canMerge, canSplit, key: `${instance.state.selection.from}:${instance.state.selection.to}` };
    },
  });

  const floating = useFloating({
    middleware: [offset(4)],
    open: selectionState !== null,
    placement: "bottom-end",
    strategy: "fixed",
  });
  const { refs, update } = floating;

  useEffect(() => {
    if (!selectionState) return;
    const cells = editor.view.dom.querySelectorAll(".selectedCell");
    if (cells.length === 0) return;
    let rect = cells[0]!.getBoundingClientRect();
    cells.forEach((cell) => {
      const r = cell.getBoundingClientRect();
      rect = new DOMRect(
        Math.min(rect.x, r.x),
        Math.min(rect.y, r.y),
        Math.max(rect.right, r.right) - Math.min(rect.x, r.x),
        Math.max(rect.bottom, r.bottom) - Math.min(rect.y, r.y),
      );
    });
    const frozen = rect;
    refs.setReference({ getBoundingClientRect: () => frozen });
    update();
  }, [editor, refs, selectionState, update]);

  // Scrolling shifts the frozen rect out from under the control — recompute.
  useEffect(() => {
    const onScroll = () => forceTick((tick) => tick + 1);
    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, []);

  if (!selectionState || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="flex items-center gap-0.5 rounded-md border border-border/70 bg-popover p-0.5 shadow-md"
      ref={refs.setFloating}
      style={{ ...floating.floatingStyles, zIndex: 50 }}
    >
      {selectionState.canMerge ? (
        <button
          className="flex h-6 items-center gap-1 rounded px-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          onClick={() => editor.chain().focus().mergeCells().run()}
          type="button"
        >
          <Merge className="size-3" />
          {t("editor.merge")}
        </button>
      ) : null}
      {selectionState.canSplit ? (
        <button
          className="flex h-6 items-center gap-1 rounded px-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          onClick={() => editor.chain().focus().splitCell().run()}
          type="button"
        >
          <Split className="size-3" />
          {t("editor.split")}
        </button>
      ) : null}
    </div>,
    document.body,
  );
}

export function TableHandles({ editor }: { editor: Editor }) {
  const state = useTableHandlesState(editor);
  const [menuOpen, setMenuOpen] = useState<"col" | "row" | null>(null);
  const [isExtendDragging, setIsExtendDragging] = useState(false);

  const dragging = state?.draggingState;
  const show = state?.show ?? false;
  const cellRect = state?.referencePosCell ?? null;
  const tableRect = state?.referencePosTable ?? null;

  const rowFloating = useHandlePosition("row", show, cellRect, tableRect, dragging);
  const colFloating = useHandlePosition("col", show, cellRect, tableRect, dragging);
  const rowExtendFloating = useExtendPosition("row", show, tableRect);
  const colExtendFloating = useExtendPosition("column", show, tableRect);

  const handleRowMenuChange = useCallback((open: boolean) => setMenuOpen(open ? "row" : null), []);
  const handleColMenuChange = useCallback((open: boolean) => setMenuOpen(open ? "col" : null), []);

  const hasCell = state !== null && cellRect !== null;
  const showRowHandle =
    (show && hasCell && typeof state.rowIndex === "number" && menuOpen !== "col" && !isExtendDragging) || menuOpen === "row";
  const showColHandle =
    (show && hasCell && typeof state.colIndex === "number" && menuOpen !== "row" && !isExtendDragging) || menuOpen === "col";
  const showRowExtend = show && state !== null && state.showAddOrRemoveRowsButton && menuOpen === null;
  const showColExtend = show && state !== null && state.showAddOrRemoveColumnsButton && menuOpen === null;

  const portalTarget = useMemo(() => (typeof document === "undefined" ? null : document.body), []);
  if (!state || !portalTarget) return <CellSelectionControls editor={editor} />;

  const handlesPortal = createPortal(
    // Leaving the handles anywhere but back into the editor dismisses them —
    // they are body portals and would otherwise linger over unrelated UI.
    <div
      data-table-handles
      onMouseLeave={(event) => {
        const next = event.relatedTarget;
        if (next instanceof Element && editor.view.dom.contains(next)) return;
        hideTableHandles(editor);
      }}
    >
      {showRowHandle && typeof state.rowIndex === "number" ? (
        <div ref={rowFloating.refs.setFloating} style={{ ...rowFloating.floatingStyles, zIndex: 50 }}>
          <HandleMenu
            editor={editor}
            index={state.rowIndex}
            onOpenChange={handleRowMenuChange}
            open={menuOpen === "row"}
            orientation="row"
            tablePos={state.blockPos}
          />
        </div>
      ) : null}

      {showColHandle && typeof state.colIndex === "number" ? (
        <div ref={colFloating.refs.setFloating} style={{ ...colFloating.floatingStyles, zIndex: 50 }}>
          <HandleMenu
            editor={editor}
            index={state.colIndex}
            onOpenChange={handleColMenuChange}
            open={menuOpen === "col"}
            orientation="column"
            tablePos={state.blockPos}
          />
        </div>
      ) : null}

      {showRowExtend ? (
        <div ref={rowExtendFloating.refs.setFloating} style={{ ...rowExtendFloating.floatingStyles, zIndex: 50 }}>
          <ExtendButton editor={editor} onDragStateChange={setIsExtendDragging} orientation="row" state={state} tableSize={tableRect?.width ?? 0} />
        </div>
      ) : null}

      {showColExtend ? (
        <div ref={colExtendFloating.refs.setFloating} style={{ ...colExtendFloating.floatingStyles, zIndex: 50 }}>
          <ExtendButton editor={editor} onDragStateChange={setIsExtendDragging} orientation="column" state={state} tableSize={tableRect?.height ?? 0} />
        </div>
      ) : null}
    </div>,
    portalTarget,
  );

  return (
    <>
      <CellSelectionControls editor={editor} />
      {handlesPortal}
    </>
  );
}
